import React, { useState } from "react";
import { colors, font, displayName } from "../utils/theme.js";
import { inputStyle, primaryBtn, ghostBtn, STATUSES } from "./ui.js";
import { valueToAssignee } from "./InlineTaskFields.jsx";

// Sticky bar shown while tasks are selected on an Objectives & Tasks list (spec 2026-10-27 §B).
// Presentational: each panel hands the parent one action shaped for bulkFields(); the parent
// applies it to the selection. clientId=null (internal work) hides Client/Private options.
export default function BulkBar({ count, users, clientId, objectives = [], busy, onApply, onDelete, onClear }) {
  const [panel, setPanel] = useState(null); // "assignee" | "status" | "dates" | "private" | "move"
  if (!count) return null;
  const toggle = (p) => setPanel((cur) => (cur === p ? null : p));
  const apply = (action) => { setPanel(null); onApply(action); };
  const btn = (p, label) => (
    <button key={p} disabled={busy} onClick={() => toggle(p)}
      style={{ ...ghostBtn, padding: "5px 10px", fontSize: 12, borderColor: panel === p ? colors.accent : colors.borderLight, color: panel === p ? colors.text : colors.textMuted }}>{label}</button>
  );
  return (
    <div style={{ position: "sticky", top: 0, zIndex: 5, background: colors.surface, border: `1px solid ${colors.accent}`, borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: colors.text, marginRight: 4 }}>{count} selected</span>
        {btn("assignee", "Assignee")}
        {btn("status", "Status")}
        {btn("dates", "Dates")}
        {clientId && btn("private", "Private")}
        {btn("move", "Move")}
        <button disabled={busy} onClick={() => { setPanel(null); onDelete(); }}
          style={{ ...ghostBtn, padding: "5px 10px", fontSize: 12, color: colors.danger, borderColor: "rgba(239,68,68,0.4)" }}>Delete</button>
        <button onClick={() => { setPanel(null); onClear(); }}
          style={{ background: "none", border: "none", color: colors.textDim, fontSize: 12, cursor: "pointer", fontFamily: font, marginLeft: "auto" }}>Clear</button>
        {busy && <span style={{ fontSize: 12, color: colors.textDim }}>Saving…</span>}
      </div>
      {panel === "assignee" && <AssigneePanel users={users} clientId={clientId} busy={busy} onApply={apply} />}
      {panel === "status" && <StatusPanel busy={busy} onApply={apply} />}
      {panel === "dates" && <DatesPanel busy={busy} onApply={apply} />}
      {panel === "private" && (
        <Panel>
          <button disabled={busy} onClick={() => apply({ kind: "private", value: true })} style={primaryBtn}>Make private</button>
          <button disabled={busy} onClick={() => apply({ kind: "private", value: false })} style={ghostBtn}>Make visible to client</button>
        </Panel>
      )}
      {panel === "move" && <MovePanel objectives={objectives} busy={busy} onApply={apply} />}
    </div>
  );
}

function Panel({ children }) {
  return <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 8, paddingTop: 8, borderTop: `1px solid ${colors.border}` }}>{children}</div>;
}
const small = { ...inputStyle, width: "auto", padding: "5px 8px", fontSize: 12 };

function AssigneePanel({ users, clientId, busy, onApply }) {
  const [val, setVal] = useState(""); // explicit choice required — no one-click unassign
  return (
    <Panel>
      <select aria-label="New assignee" style={small} value={val} onChange={(e) => setVal(e.target.value)}>
        <option value="" disabled>Choose assignee…</option>
        <option value="unassigned">Unassigned</option>
        {clientId && <option value="client">Client</option>}
        {(users || []).map((u) => <option key={u.id} value={u.id}>{displayName(u)}</option>)}
      </select>
      <button disabled={busy || !val} onClick={() => onApply({ kind: "assignee", assignee: valueToAssignee(val, clientId) })} style={{ ...primaryBtn, opacity: val ? 1 : 0.5 }}>Apply</button>
    </Panel>
  );
}

function StatusPanel({ busy, onApply }) {
  const [val, setVal] = useState(""); // explicit choice required — no one-click reset
  return (
    <Panel>
      <select aria-label="New status" style={small} value={val} onChange={(e) => setVal(e.target.value)}>
        <option value="" disabled>Choose status…</option>
        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <button disabled={busy || !val} onClick={() => onApply({ kind: "status", status: val })} style={{ ...primaryBtn, opacity: val ? 1 : 0.5 }}>Apply</button>
    </Panel>
  );
}

function DatesPanel({ busy, onApply }) {
  const [mode, setMode] = useState("set");
  const [start, setStart] = useState("");
  const [due, setDue] = useState("");
  const [clearStart, setClearStart] = useState(false);
  const [clearDue, setClearDue] = useState(false);
  const [days, setDays] = useState("");
  const action = { kind: "setDates" };
  if (clearStart) action.startDate = null; else if (start) action.startDate = start;
  if (clearDue) action.dueDate = null; else if (due) action.dueDate = due;
  const setReady = "startDate" in action || "dueDate" in action;
  const n = Number(days);
  const shiftReady = days !== "" && Number.isInteger(n) && n !== 0;
  const tab = (m, label) => (
    <button onClick={() => setMode(m)} style={{ ...ghostBtn, padding: "4px 10px", fontSize: 12, borderColor: mode === m ? colors.accent : colors.borderLight, color: mode === m ? colors.text : colors.textMuted }}>{label}</button>
  );
  const lbl = { display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: colors.textMuted };
  return (
    <Panel>
      {tab("set", "Set")}{tab("shift", "Shift")}
      {mode === "set" ? (
        <>
          <span style={lbl}>Start <input type="date" aria-label="New start date" style={small} value={start} disabled={clearStart} onChange={(e) => setStart(e.target.value)} /></span>
          <label style={lbl}><input type="checkbox" aria-label="Clear start" checked={clearStart} onChange={(e) => setClearStart(e.target.checked)} />clear</label>
          <span style={lbl}>Due <input type="date" aria-label="New due date" style={small} value={due} disabled={clearDue} onChange={(e) => setDue(e.target.value)} /></span>
          <label style={lbl}><input type="checkbox" aria-label="Clear due" checked={clearDue} onChange={(e) => setClearDue(e.target.checked)} />clear</label>
          <button disabled={busy || !setReady} onClick={() => onApply(action)} style={{ ...primaryBtn, opacity: setReady ? 1 : 0.5 }}>Apply</button>
        </>
      ) : (
        <>
          <input type="number" step="1" aria-label="Working days" placeholder="e.g. 5 or -3" style={{ ...small, width: 110 }} value={days} onChange={(e) => setDays(e.target.value)} />
          <button disabled={busy || !shiftReady} onClick={() => onApply({ kind: "shiftDates", days: n })} style={{ ...primaryBtn, opacity: shiftReady ? 1 : 0.5 }}>Apply</button>
          <span style={{ fontSize: 11.5, color: colors.textDim }}>Moves start and due dates by working days (Mon–Fri). Blank dates stay blank.</span>
        </>
      )}
    </Panel>
  );
}

function MovePanel({ objectives, busy, onApply }) {
  const [id, setId] = useState("");
  const groups = [];
  for (const o of objectives) {
    const g = o.group || "";
    let entry = groups.find((x) => x.name === g);
    if (!entry) { entry = { name: g, items: [] }; groups.push(entry); }
    entry.items.push(o);
  }
  const target = objectives.find((o) => o.id === id);
  return (
    <Panel>
      <select aria-label="Move to objective" style={small} value={id} onChange={(e) => setId(e.target.value)}>
        <option value="">Choose objective…</option>
        {groups.map((g) => (g.name
          ? <optgroup key={g.name} label={g.name}>{g.items.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</optgroup>
          : g.items.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)))}
      </select>
      <button disabled={busy || !target} onClick={() => onApply({ kind: "move", objectiveId: target.id, projectId: target.projectId ?? null })} style={{ ...primaryBtn, opacity: target ? 1 : 0.5 }}>Apply</button>
    </Panel>
  );
}
